/**
 * adminClaims.ts
 * 
 * This utility file handles checking if a user has admin claims
 * and updating the AppContext accordingly.
 */
import { User } from 'firebase/auth';
import { logger } from './logger';

/**
 * Checks if the user has admin role in their custom claims
 * @param user The Firebase user to check
 * @returns Promise that resolves with a boolean indicating if user is an admin
 */
export const checkAdminClaims = async (user: User): Promise<boolean> => {
  try {
    if (!user) return false;
    
    // Get the token result which contains the claims
    const tokenResult = await user.getIdTokenResult(true); // Force token refresh
    
    // Log detailed token claims for debugging
    logger.info('🔐 User token claims:', { 
      uid: user.uid,
      hasRole: tokenResult.claims && 'role' in tokenResult.claims,
      role: tokenResult.claims?.role || 'none',
      claims: JSON.stringify(tokenResult.claims)
    });
    
    // Log the actual admin claim value
    logger.info('🔐 Admin claim check:', { 
      hasAdminClaim: !!tokenResult.claims?.admin,
      adminValue: tokenResult.claims?.admin,
      roleValue: tokenResult.claims?.role
    });
    
    // Check if the user has the admin role (check both methods)
    if (tokenResult.claims && tokenResult.claims.admin === true) {
      logger.info('👑 User has admin=true claim in Firebase');
      return true;
    }
    
    if (tokenResult.claims && tokenResult.claims.role === 'admin') {
      logger.info('👑 User has role=admin in Firebase claims');
      return true;
    } 
    
    // Log warnings for debugging
    if (tokenResult.claims && tokenResult.claims.role && tokenResult.claims.role !== 'admin') {
      logger.warn('⚠️ User has role claims but is not admin:', tokenResult.claims.role);
    } else if (!tokenResult.claims || !('role' in tokenResult.claims)) {
      logger.warn('⚠️ User has no role claims defined');
    }
    
    if (tokenResult.claims && tokenResult.claims.admin === false) {
      logger.warn('⚠️ User has admin claim but it is explicitly set to false');
    } else if (tokenResult.claims && !('admin' in tokenResult.claims)) {
      logger.warn('⚠️ User has no admin claim defined');
    }
    
    return false;
  } catch (error) {
    logger.error('❌ Error checking admin claims:', error);
    return false;
  }
};
